import { sortRecords } from "@smartsuite/fields-logic/lib/sort";
import {
  AppField,
  AppStructureFieldParams,
  FieldChoiceValue,
  Filter,
  FilterField,
  SimpleRecord,
  SortRule,
} from "@smartsuite/types";
import { isArray } from "lodash-es";
import { mockPreferences } from "../../../../config/preferences";
import { FormConfigApplicationStructureItem, FormConfigItem } from "../../../../types/form";

interface SelectedFormValues {
  [key: string]: Array<{ id: string }>;
}

export const convertFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): FilterField => {
  if (!filterField.value) {
    return filterField;
  }

  if (isArray(filterField.value)) {
    return convertArrayFilterValue(filterField, selectedFormValues);
  } else {
    return convertStringFilterValue(filterField, selectedFormValues);
  }
};

export const convertArrayFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): FilterField =>
  filterField?.value?.reduce((acc: string[], oldValue: string) => {
    if (selectedFormValues[oldValue]) {
      return [...acc, ...selectedFormValues[oldValue].map((v) => v.id)];
    }
    return acc;
  }, []);

export const convertStringFilterValue = (
  filterField: FilterField,
  selectedFormValues: SelectedFormValues
): FilterField => {
  const dynamicTextFilterVarRegex = /(\[\[[a-z0-9_]+\]\])/g;

  const tokens = filterField.value.split(dynamicTextFilterVarRegex);

  return tokens.reduce((acc: string[], token: string) => {
    if (dynamicTextFilterVarRegex.test(token)) {
      const value = token.slice(2, -2);
      const selectedValueId = selectedFormValues[value]?.map((v) => v.id);
      if (selectedValueId) {
        return [...acc, ...selectedValueId];
      }
    } else {
      if (token) {
        return [...acc, token];
      }
    }

    return acc;
  }, []);
};

interface GetSortedRecordsParams {
  records: SimpleRecord[];
  formItem: FormConfigItem;
  field: FormConfigApplicationStructureItem;
  linkedApplicationFields?: {
    [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
  };
}

export const getSortedRecords = ({
  records,
  formItem,
  field,
  linkedApplicationFields,
}: GetSortedRecordsParams): SimpleRecord[] => {
  const sortData = getSortData(formItem, field);

  const subItemsFields = Object.values(linkedApplicationFields ?? {}).filter(
    (field) => field.field_type === "subitemsfield"
  );

  const parseRecords = (records: SimpleRecord[]): SimpleRecord[] => {
    if (subItemsFields.length === 0) {
      return records;
    }

    return records.map((record) => {
      const subItems = subItemsFields.reduce(
        (acc, field) => ({
          ...acc,
          [field.slug]: { count: record[field.slug]?.items?.length || 0 },
        }),
        {}
      );

      return { ...record, ...subItems };
    });
  };

  const parsedRecords = parseRecords(records);

  const sortedRecords =
    sortData && parsedRecords.length && linkedApplicationFields
      ? sortRecords(
          parsedRecords,
          Object.values(linkedApplicationFields ?? {}),
          sortData,
          mockPreferences
        )
      : records;

  return sortedRecords;
};

const MAX_VISIBLE_FIELDS = 4;

export const getVisibleFields = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem
): string[] => {
  const visibleFields: string[] =
    formItem.params.visible_fields ??
    field.params?.select_record_modal_visible_fields ??
    field.params?.visible_fields ??
    [];

  return visibleFields.filter((field) => field !== "title").slice(0, MAX_VISIBLE_FIELDS);
};

export const getSortData = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem
): SortRule[] => {
  return (
    formItem.params?.sort_data ??
    field.params?.select_record_modal_sort_data ??
    field.params?.sort_data
  );
};

export const getFieldsInUse = (
  formItem: FormConfigItem,
  field: FormConfigApplicationStructureItem,
  filterData?: Filter
): Set<string> => {
  const fieldsInUse = new Set<string>();

  const recordSelectType = formItem.params?.record_select_type;

  fieldsInUse.add("title");

  if (recordSelectType !== "dropdown") {
    const visibleFields = getVisibleFields(formItem, field);
    visibleFields.forEach((field: string) => fieldsInUse.add(field));

    const coverImageField =
      formItem.params?.cover_image_field ??
      (typeof field.params === "object" && "cover_image_field" in field.params
        ? (field.params as { cover_image_field?: string }).cover_image_field
        : undefined);

    if (coverImageField) {
      fieldsInUse.add(coverImageField);
    }
  }

  if (filterData) {
    filterData.fields.forEach((filterField) => {
      fieldsInUse.add(filterField.field);
    });
  }

  const sortData = getSortData(formItem, field);
  if (sortData) {
    sortData.forEach((sortField: { field: string }) => {
      fieldsInUse.add(sortField.field);
    });
  }

  return fieldsInUse;
};
